// Shared loader for the tenant's configurable facility value sets
// (`/api/config/facility-config`): work-order statuses, categories, and
// work-order types. These used to be hard-coded constants; they are now
// tenant-editable (Settings → Facilities). While the fetch is in flight — or if
// it fails — we fall back to the built-in defaults so the queue always renders.
//
// Consumed two ways:
//   • useFacilityConfig() — fetches and returns the index (used at a page root).
//   • <FacilityConfigProvider> + useFacilityOptions() — provide once per page so
//     deep components (status badges, wo-type pills, move selects) resolve labels
//     without prop-drilling. Outside a provider useFacilityOptions() yields the
//     built-in defaults, so isolated renders still work.

import { createContext, useContext, useMemo, type ReactNode } from "react";
import {
  getFacilityConfig,
  type FacilityOption,
  type FacilityBuildingOption,
  type FacilityRoomOption,
} from "./api";
import { useApi } from "./useApi";
import { FACILITY_STATUSES, FACILITY_CATEGORIES, FACILITY_WO_TYPES, FACILITY_PRIORITIES } from "./constants";

export interface FacilityConfigIndex {
  statuses: FacilityOption[];
  categories: FacilityOption[];
  woTypes: FacilityOption[];
  priorities: FacilityOption[];
  /** Shared Campus → Building → Room cascade (also used by tickets). Empty until
   *  the tenant configures it. */
  campuses: FacilityOption[];
  buildings: FacilityBuildingOption[];
  rooms: FacilityRoomOption[];
  /** Resolve a code → its label within a given list (falls back to the code). */
  labelOf: (list: FacilityOption[], code: string | null | undefined) => string;
  loading: boolean;
}

/** The built-in defaults, as an index — also the context's default value so
 *  components used outside a provider still resolve sensible labels. */
const DEFAULT_INDEX: FacilityConfigIndex = {
  statuses: FACILITY_STATUSES,
  categories: FACILITY_CATEGORIES,
  woTypes: FACILITY_WO_TYPES,
  priorities: FACILITY_PRIORITIES,
  campuses: [],
  buildings: [],
  rooms: [],
  labelOf: (list, code) => list.find((o) => o.code === code)?.label ?? code ?? "—",
  loading: false,
};

/** Load the tenant's facility value sets once and expose them + a label
 *  resolver. Defaults stand in until the fetch resolves. */
export function useFacilityConfig(): FacilityConfigIndex {
  const { data, loading } = useApi((signal) => getFacilityConfig(signal), []);
  return useMemo<FacilityConfigIndex>(() => {
    const cfg = data?.config;
    return {
      statuses: cfg?.statuses?.length ? cfg.statuses : FACILITY_STATUSES,
      categories: cfg?.categories?.length ? cfg.categories : FACILITY_CATEGORIES,
      woTypes: cfg?.wo_types?.length ? cfg.wo_types : FACILITY_WO_TYPES,
      priorities: cfg?.priorities?.length ? cfg.priorities : FACILITY_PRIORITIES,
      campuses: cfg?.campuses ?? [],
      buildings: cfg?.buildings ?? [],
      rooms: cfg?.rooms ?? [],
      labelOf: DEFAULT_INDEX.labelOf,
      loading,
    };
  }, [data, loading]);
}

const FacilityConfigContext = createContext<FacilityConfigIndex>(DEFAULT_INDEX);

/** Fetch the value sets once and share them with the subtree. Put this at a
 *  facility page root; descendants read via useFacilityOptions(). */
export function FacilityConfigProvider({ children }: { children: ReactNode }) {
  const index = useFacilityConfig();
  return <FacilityConfigContext.Provider value={index}>{children}</FacilityConfigContext.Provider>;
}

/** Read the shared facility value sets (defaults if no provider is mounted). */
export function useFacilityOptions(): FacilityConfigIndex {
  return useContext(FacilityConfigContext);
}
